var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState, useCallback } from 'react';
import { PageLayout } from '../../Components/PageLayout';
import { MainText } from '../../Components/BaseComponents/Typography';
import { MasterCardIcon, VisaIcon } from './icons';
import { HelperText, Label } from '../../Components/BaseComponents/Input';
import { useApplication } from '../../Contexts/ApplicationContext';
import { createHeaders } from '../../../Services/apiIntegrations/interceptors';
import { cardMask, splitCallbackUrl } from './utils';
import { useVgsFormCss, useVGSScript } from './hooks';
import { mercuryoScriptLoader, platform } from './directPaymentUtils';
import { storage } from '../../../Services/storage';
import { observer } from 'mobx-react-lite';
import { SelectCard } from './SelectCard';
import styles from './direct.module.css';
import { Checkbox } from '../../Components/BaseComponents/Checkbox';
export var DirectPaymentStep = observer(function () {
    var _a = useState(undefined), selectedCard = _a[0], setSelectedCard = _a[1];
    var stepController = useApplication().stepController;
    var paymentInfo = useMemo(function () {
        if ('callbackUrl' in stepController.currentStep) {
            return __assign({ storedCards: stepController.currentStep.storedCards, vaultId: stepController.currentStep.vaultId }, splitCallbackUrl(stepController.currentStep.callbackUrl));
        }
        throw Error('Callback URL is required');
    }, [stepController.currentStep]);
    var vgsScriptLoaded = useVGSScript().vgsScriptLoaded;
    useEffect(function () {
        mercuryoScriptLoader(storage.ORDER_ID.get());
    }, []);
    var canSelectCard = Boolean(paymentInfo.storedCards.length);
    var shouldSelectCard = canSelectCard && !selectedCard;
    if (shouldSelectCard) {
        return (_jsx(SelectCard, { cards: paymentInfo.storedCards, onCardSelect: function (card) { return setSelectedCard(card); } }));
    }
    return (_jsx(CardPayment, { CName: paymentInfo.CName, vaultId: paymentInfo.vaultId, endpointPath: paymentInfo.path, isReady: vgsScriptLoaded, cardId: selectedCard === null || selectedCard === void 0 ? void 0 : selectedCard.id, cardNumber: selectedCard === null || selectedCard === void 0 ? void 0 : selectedCard.cardNumber, onBackClick: canSelectCard ? function () { return setSelectedCard(undefined); } : undefined }));
});
var CardPayment = function (_a) {
    var isReady = _a.isReady, endpointPath = _a.endpointPath, vaultId = _a.vaultId, CName = _a.CName, cardId = _a.cardId, onBackClick = _a.onBackClick, cardNumber = _a.cardNumber;
    var _b = useState(), form = _b[0], setForm = _b[1];
    var _c = useState(false), btnLoader = _c[0], setBtnLoader = _c[1];
    var _d = useState(false), fetchError = _d[0], setFetchError = _d[1];
    var _e = useState(false), submitEnable = _e[0], setSubmitEnable = _e[1];
    var _f = useState(true), saveCard = _f[0], setSaveCard = _f[1];
    var stepController = useApplication().stepController;
    var _g = useVgsFormCss(), fieldCss = _g.fieldCss, smallFieldCss = _g.smallFieldCss;
    useEffect(function () {
        var _a;
        if (isReady) {
            var form_1 = (_a = window.VGSCollect) === null || _a === void 0 ? void 0 : _a.create(vaultId, 'sandbox', function (data) {
                var _a, _b, _c, _d, _e;
                var readyToSubmit = (((_a = data.expiration) === null || _a === void 0 ? void 0 : _a.isValid) &&
                    ((_b = data.cardholder) === null || _b === void 0 ? void 0 : _b.isValid) &&
                    ((_c = data.cardNumber) === null || _c === void 0 ? void 0 : _c.isValid) &&
                    ((_d = data.cvc) === null || _d === void 0 ? void 0 : _d.isValid)) ||
                    (((_e = data.cvc) === null || _e === void 0 ? void 0 : _e.isValid) && cardId);
                setSubmitEnable(readyToSubmit);
            }).useCname(CName);
            if (!cardId) {
                form_1
                    .field('#card-name', {
                    type: 'text',
                    name: 'cardholder',
                    placeholder: 'John Doe',
                    validations: ['required'],
                    autoComplete: 'cc-name',
                    css: fieldCss,
                })
                    .replacePattern('/[^a-zA-Z\\s]+/g');
                form_1.field('#card-number', {
                    type: 'card-number',
                    name: 'cardNumber',
                    placeholder: '0000 0000 0000 0000',
                    validations: ['required', 'validCardNumber'],
                    autoComplete: 'cc-number',
                    css: fieldCss,
                });
                form_1.field('#card-expiry', {
                    type: 'card-expiration-date',
                    name: 'expiration',
                    placeholder: 'MM / YY',
                    validations: ['required', 'validCardExpirationDate'],
                    autoComplete: 'cc-exp',
                    css: smallFieldCss,
                });
            }
            form_1.field('#card-cvc', {
                type: 'card-security-code',
                name: 'cvc',
                placeholder: '•••',
                maxLength: 3,
                validations: ['required', 'validCardSecurityCode'],
                css: smallFieldCss,
            });
            setForm(form_1);
        }
    }, [smallFieldCss, fieldCss, CName, vaultId, isReady, cardId]);
    var handleFormSubmit = useCallback(function () {
        setBtnLoader(true);
        setFetchError(false);
        form.submit(endpointPath, {
            data: function (formValues) {
                return {
                    cardDetails: formValues,
                    af_cart_token: storage.ORDER_ID.get(),
                    af_platform: platform(),
                    saveCard: saveCard,
                    savedCard: cardId,
                };
            },
            headers: createHeaders(true),
        }, function (status, res) {
            setBtnLoader(false);
            if ('nextStep' in res) {
                stepController.updateStepInfo(res);
            }
            else {
                setFetchError(true);
            }
        }, function () {
            setFetchError(true);
            setBtnLoader(false);
        });
    }, [form, endpointPath, saveCard, stepController, cardId]);
    useEffect(function () {
        if (form) {
            form.on('enterPress', handleFormSubmit);
            return function () { return form.off('enterPress', handleFormSubmit); };
        }
    }, [form, handleFormSubmit]);
    return (_jsxs(PageLayout, __assign({ customBackClick: onBackClick, loading: !isReady, hideHeaderChip: true, footer: {
            actionButtonLabel: 'Confirm',
            buttonClick: handleFormSubmit,
            actionButtonLoader: btnLoader,
            actionButtonDisable: !submitEnable,
            actionBtnError: fetchError
                ? 'Something went wrong. Try again'
                : undefined,
        } }, { children: [_jsx(MainText, __assign({ style: { marginBottom: 20 } }, { children: "Pay by bank card" })), cardId ? _jsx(SavedCardForm, { cardNumber: cardNumber }) : _jsx(NewCardForm, {}), !cardId && (_jsx("div", __assign({ className: styles.save_card }, { children: _jsx(Checkbox, __assign({ onChange: function () { return setSaveCard(function (v) { return !v; }); }, checked: saveCard }, { children: "Save for the next payments" })) }))), _jsxs("div", __assign({ style: cardId ? { marginTop: 160 } : undefined, className: styles.captcha_wrapper }, { children: [_jsx("div", __assign({ className: styles.captcha }, { children: "Supported by" })), _jsx(VisaIcon, {}), _jsx(MasterCardIcon, {})] }))] })));
};
var NewCardForm = function () {
    return (_jsxs(_Fragment, { children: [_jsxs("div", { children: [_jsx(Label, { children: "Cardholder name" }), _jsx("div", { className: styles.field, id: "card-name" }), _jsx(HelperText, {})] }), _jsxs("div", { children: [_jsx(Label, { children: "Card number" }), _jsx("div", { className: styles.field, id: "card-number" }), _jsx(HelperText, {})] }), _jsxs("div", __assign({ style: { display: 'flex' } }, { children: [_jsxs("div", __assign({ style: { width: '50%' } }, { children: [_jsx(Label, { children: "Expiry date" }), _jsx("div", { className: styles.field, id: "card-expiry" })] })), _jsxs("div", __assign({ style: { width: '50%' } }, { children: [_jsx(Label, { children: "CVV/CVC" }), _jsx("div", { className: styles.field, id: "card-cvc" })] }))] }))] }));
};
export var SavedCardForm = function (_a) {
    var cardNumber = _a.cardNumber;
    return (_jsxs(_Fragment, { children: [_jsxs("div", { children: [_jsx(Label, { children: "Card number" }), _jsx("div", __assign({ className: styles.fake_field }, { children: cardMask(cardNumber) })), _jsx(HelperText, {})] }), _jsx("div", __assign({ style: { display: 'flex', justifyContent: 'flex-start' } }, { children: _jsxs("div", __assign({ style: { width: '50%' } }, { children: [_jsx(Label, { children: "CVV/CVC" }), _jsx("div", { className: styles.field, id: "card-cvc" })] })) }))] }));
};
